import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Check, Plus, RotateCcw, Loader2 } from 'lucide-react';

export interface SimilarIngrediente {
  id: number;
  nombre: string;
  categoria: string;
  stock_actual?: number;
  activo: boolean;
  unidad_abreviacion?: string;
}

export interface ConflictoSimilar {
  detalleIndex: number;
  nombreBuscado: string;
  similares: SimilarIngrediente[];
}

export type DecisionConflicto =
  | { tipo: 'usar_existente'; ingrediente: SimilarIngrediente }
  | { tipo: 'crear_nuevo' };

interface ModalVerificarIngredientesProps {
  isOpen: boolean;
  conflictos: ConflictoSimilar[];
  onConfirmar: (decisiones: Map<number, DecisionConflicto>) => void;
  onCancelar: () => void;
  loading?: boolean;
}

const ModalVerificarIngredientes: React.FC<ModalVerificarIngredientesProps> = ({
  isOpen,
  conflictos,
  onConfirmar,
  onCancelar,
  loading = false,
}) => {
  const [decisiones, setDecisiones] = useState<Map<number, DecisionConflicto>>(new Map());

  // Resetear decisiones cada vez que el modal se abre con nuevos conflictos.
  // Sin esto, las decisiones de una sesión previa contaminarían la siguiente apertura,
  // provocando que el botón "Guardar Factura" quede habilitado prematuramente.
  useEffect(() => {
    if (isOpen) {
      setDecisiones(new Map());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const todasDecididas = conflictos.every((c) => decisiones.has(c.detalleIndex));
  const resueltasCount = decisiones.size;

  const setDecision = (detalleIndex: number, decision: DecisionConflicto) => {
    setDecisiones((prev) => new Map(prev).set(detalleIndex, decision));
  };

  const getDecision = (detalleIndex: number) => decisiones.get(detalleIndex);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-verificar-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div>
              <h3
                id="modal-verificar-title"
                className="text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                Revisar antes de guardar
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Se encontraron ingredientes similares que podrían ser duplicados
              </p>
            </div>
          </div>
          <button
            onClick={onCancelar}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer flex-shrink-0"
            aria-label="Cancelar y volver a editar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          {conflictos.map((conflicto) => {
            const decision = getDecision(conflicto.detalleIndex);
            const resuelta = !!decision;

            return (
              <div
                key={conflicto.detalleIndex}
                className={`border rounded-lg p-4 transition-colors ${
                  resuelta
                    ? 'border-green-300 dark:border-green-700 bg-green-50/40 dark:bg-green-900/10'
                    : 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10'
                }`}
              >
                {/* Encabezado del conflicto */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      En la factura:
                    </span>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">
                      {conflicto.nombreBuscado}
                    </p>
                  </div>
                  {/* Badge de decisión tomada */}
                  {decision && (
                    <div
                      className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${
                        decision.tipo === 'usar_existente'
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                      }`}
                    >
                      <Check className="w-3 h-3" />
                      {decision.tipo === 'usar_existente'
                        ? `Usará: ${decision.ingrediente.nombre}`
                        : 'Creará nuevo'}
                    </div>
                  )}
                </div>

                {/* Lista de similares */}
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Ingredientes similares en el sistema:
                </p>
                <div className="space-y-2">
                  {conflicto.similares.map((similar) => {
                    const seleccionado =
                      decision?.tipo === 'usar_existente' &&
                      decision.ingrediente.id === similar.id;

                    return (
                      <div
                        key={similar.id}
                        className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${
                          seleccionado
                            ? 'border-green-400 dark:border-green-600 bg-green-100/80 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/60'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {similar.nombre}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {similar.categoria}
                            {similar.stock_actual !== undefined && (
                              <span>
                                {' · '}Stock:{' '}
                                {similar.stock_actual.toLocaleString('es-CL')}{' '}
                                {similar.unidad_abreviacion || 'u'}
                              </span>
                            )}
                            {!similar.activo && (
                              <span className="ml-1 text-red-500 font-medium">
                                (inactivo)
                              </span>
                            )}
                          </p>
                        </div>

                        <button
                          onClick={() =>
                            setDecision(conflicto.detalleIndex, {
                              tipo: 'usar_existente',
                              ingrediente: similar,
                            })
                          }
                          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs
                            font-medium transition-colors cursor-pointer ${
                              !similar.activo
                                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                            }`}
                        >
                          {!similar.activo ? (
                            <RotateCcw className="w-3 h-3" />
                          ) : (
                            <Check className="w-3 h-3" />
                          )}
                          {!similar.activo ? 'Reactivar y usar' : 'Usar este'}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Opción crear nuevo */}
                <button
                  onClick={() =>
                    setDecision(conflicto.detalleIndex, { tipo: 'crear_nuevo' })
                  }
                  className={`mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg
                    text-xs font-medium border transition-colors cursor-pointer ${
                      decision?.tipo === 'crear_nuevo'
                        ? 'border-blue-400 dark:border-blue-500 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  <Plus className="w-3 h-3" />
                  Crear nuevo: "{conflicto.nombreBuscado}"
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {resueltasCount}/{conflictos.length} resueltos
            {!todasDecididas && (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                — decide por cada ingrediente para continuar
              </span>
            )}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancelar}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900
                         dark:hover:text-gray-100 transition-colors cursor-pointer"
            >
              Volver a editar
            </button>
            <button
              onClick={() => todasDecididas && !loading && onConfirmar(decisiones)}
              disabled={!todasDecididas || loading}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-colors ${
                todasDecididas && !loading
                  ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Guardar Factura
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalVerificarIngredientes;
